import { Fragment, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { visuallyHidden } from '@mui/utils';
import { Button } from '@/ui';
import {
  BulkAction,
  CapInstance,
  CapTypes,
  CriteriaPaneProps,
  CriteriaState,
  RestrictionTypes,
  SourceType,
} from '../types';
import {
  convertArrayToDayParting,
  convertDayPartingToArray,
  CriteriaSection,
  DayPartingEntry,
  FIELD_LABEL_CLASS,
  FieldGrid,
  FieldLabel,
  Input,
  MultiSelect,
  ReadOnlyValue,
  SelectSimple,
  TimeSelect,
} from '../FormComponents';
import { BULK_ACTION_DAY_MAPPING, capIntervalOptions, DAY_TO_DOW_TYPE, restrictionTypes } from '../constants';
import { BulkDayPartingModal } from '../BulkDayPartingModal';
import { useOfferActionOptions, useOfferOptions } from '../useCriteriaOptions';
import { HELP } from '../criteriaHelp';
import { errorReader, hasValue } from './shared';
import type { CriteriaSectionDef } from './types';
import { useCurrentCapAmount } from '../../../../hooks/useCurrentCapAmount';
import { useCapConfiguration } from '../../../../hooks/useCapConfiguration';
import { getOfferSelector } from '../../../../reducers/OfferTitleReducer';
// Cap group UI moved here from Post/Linkout/UserAttribute (still uses offerActionDetail redux-form fields).
import CapGroupSection from '../../../../views/Offers/OfferActions/CapGroupSection';

const NO_REDIS_VALUE = 'N/A';

/** Day parting reads as a table once its panel is this wide; narrower, each row labels itself. */
const DAY_TABLE_QUERY = '@container dayparting (min-width: 46rem)';
const DAY_COLUMNS = ['Restriction', 'Start Time', 'End Time', 'Cap Amount', 'Current Cap Amount'];

/**
 * The live cap counters in Redis for whatever the criteria belong to. Offers are keyed by
 * offer ID; offer actions by their offerCriteria ID, not the action ID.
 */
function useLiveCaps(sourceType: SourceType) {
  const { offerId } = useParams();
  const isOffer = sourceType === 'offer';
  const offerCriteriaId = useSelector((s: any) => s.offerAction?.offerAction?.offerCriteria?.id);
  const capEntityId = sourceType === 'offerWallAd' ? undefined : isOffer ? offerId : offerCriteriaId;
  const { data: dailyAmount } = useCurrentCapAmount(capEntityId, !isOffer, !!capEntityId);
  const { data: byInterval = {} } = useCapConfiguration(capEntityId, !isOffer, !!capEntityId);
  return { dailyAmount, byInterval: byInterval as Record<string, number | null | undefined> };
}

const display = (value: number | null | undefined) => (value === undefined || value === null ? NO_REDIS_VALUE : value);

/* ------------------------------------------------------------------------------ Daily Limit */

function DailyLimitSection({ state, setState, sourceType, errors }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const { dailyAmount } = useLiveCaps(sourceType);
  const limitAnswers: string[] = useSelector((s: Record<string, unknown>) => {
    const selector = getOfferSelector();
    const answers = selector ? selector(s, 'questionAnswers') : null;
    return answers ? answers.map((qa: { text?: string }) => qa?.text).filter(Boolean) : [];
  });
  const answerOptions = useMemo(() => limitAnswers.map((a) => ({ label: a, value: a })), [limitAnswers]);

  return (
    <CriteriaSection title='Daily Limit' help={HELP.dailyLimit}>
      <FieldGrid min='12rem'>
        <Input
          label='Daily Limit'
          type='number'
          value={state.dailyLimit}
          onChange={(v) => setState((s) => ({ ...s, dailyLimit: v }))}
          error={first(['criteriaDailyLimit'])}
        />
        <MultiSelect
          label='Daily Limit Answers'
          value={state.dailyLimitAnswers}
          onChange={(answers) => setState((s) => ({ ...s, dailyLimitAnswers: answers }))}
          options={answerOptions}
          placeholder='Select answers...'
          error={first(['criteriaDailyLimitAnswers'])}
          help={HELP.dailyLimitAnswers}
        />
        {state.dailyLimit && <ReadOnlyValue label='Current Daily Cap Amount' value={dailyAmount ?? ''} />}
      </FieldGrid>
    </CriteriaSection>
  );
}

/* ----------------------------------------------------------------------------- Cap Interval */

function CapIntervalSection({ state, setState, sourceType, errors }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const { dailyAmount, byInterval } = useLiveCaps(sourceType);
  // Offers cap per day through Daily Limit, so their interval list leaves Daily out.
  const intervals = useMemo(
    () => (sourceType === 'offer' ? capIntervalOptions.filter((opt) => opt.value !== 'Daily') : capIntervalOptions),
    [sourceType]
  );
  const interval = state.capInterval || '';
  const current = interval === 'Daily' ? dailyAmount : byInterval[interval.toLowerCase()];

  return (
    <CriteriaSection title='Cap Interval'>
      <FieldGrid min='12rem'>
        <SelectSimple
          label='Cap Interval'
          value={interval}
          onChange={(v) => setState((s) => ({ ...s, capInterval: v }))}
          options={intervals}
          placeholder='Select interval...'
          error={first(['capInterval'])}
          help={HELP.capInterval}
        />
        {interval && (
          <>
            <Input
              label='Cap Amount'
              type='number'
              value={state.capAmount?.toString() ?? ''}
              onChange={(v) => setState((s) => ({ ...s, capAmount: v === '' ? null : Number(v) }))}
              error={first(['capAmount'])}
            />
            <ReadOnlyValue label='Current Cap Amount' value={display(current)} help={HELP.currentCap} />
          </>
        )}
      </FieldGrid>
      {interval === 'Lifetime' && (
        <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center', color: 'warning.main' }}>
          <WarningAmber fontSize='small' />
          <Typography variant='caption'>
            Lifetime caps do not automatically reset. Once the cap is reached, the offer will become inactive until
            manually reset.
          </Typography>
        </Stack>
      )}
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------- Cap Dependencies */

function CapDependenciesSection({ state, setState }: CriteriaPaneProps) {
  const offers = useOfferOptions({ withIdPrefix: true });
  const offerActions = useOfferActionOptions({ withIdPrefix: true });
  const dependency = state.capDependency;

  const updateDependency = (patch: Partial<NonNullable<CriteriaState['capDependency']>>) =>
    setState((s) => ({
      ...s,
      capDependency: {
        requireAllCapped: s.capDependency?.requireAllCapped ?? true,
        offerIds: s.capDependency?.offerIds || [],
        offerActionIds: s.capDependency?.offerActionIds || [],
        ...patch,
      },
    }));

  return (
    <CriteriaSection title='Cap Dependencies' help={HELP.capDependencies}>
      <FormControl>
        <FieldLabel id='dependency-mode-label'>Dependency Mode</FieldLabel>
        <RadioGroup
          aria-labelledby='dependency-mode-label'
          value={(dependency?.requireAllCapped ?? true) ? 'all' : 'any'}
          onChange={(_event, next) => updateDependency({ requireAllCapped: next === 'all' })}
        >
          <FormControlLabel
            value='all'
            control={<Radio size='small' id='requireAllCapped-true' />}
            label='Require ALL dependencies capped (offer stops only when ALL selected entities are capped)'
          />
          <FormControlLabel
            value='any'
            control={<Radio size='small' id='requireAllCapped-false' />}
            label='Require ANY dependency capped (offer stops when ANY selected entity is capped)'
          />
        </RadioGroup>
      </FormControl>
      <FieldGrid min='20rem'>
        <MultiSelect
          label='Dependent Offers'
          helperText='Select offers whose cap status affects this offer.'
          value={(dependency?.offerIds || []).map(String)}
          onChange={(ids) => updateDependency({ offerIds: ids.map((v) => parseInt(v)) })}
          options={offers.options}
          placeholder='Select offers...'
          isLoading={offers.isLoading}
        />
        <MultiSelect
          label='Dependent Offer Actions'
          helperText='Select offer actions whose cap status affects this offer.'
          value={(dependency?.offerActionIds || []).map(String)}
          onChange={(ids) => updateDependency({ offerActionIds: ids.map((v) => parseInt(v)) })}
          options={offerActions.options}
          placeholder='Select offer actions...'
          isLoading={offerActions.isLoading}
        />
      </FieldGrid>
    </CriteriaSection>
  );
}

/* -------------------------------------------------------------------------------- Cap Group */

function CapGroupCriteriaSection() {
  return (
    <CriteriaSection title='Cap Group' help={HELP.capGroup}>
      <CapGroupSection />
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------- Day Partings & Caps */

const toCapRestriction = (restriction: string, start: string, end: string): CapInstance['restriction'] =>
  // Only a partial day carries a time window; Available and Unavailable have none.
  restriction === 'Partially Restricted'
    ? { type: RestrictionTypes.PARTIAL, starts: start || '', ends: end || '' }
    : null;

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

interface BulkModalState {
  isOpen: boolean;
  action: BulkAction | null;
  restriction: string;
  startTime: string;
  endTime: string;
}

const CLOSED_BULK_MODAL: BulkModalState = {
  isOpen: false,
  action: null,
  restriction: 'Available',
  startTime: '',
  endTime: '',
};

function DayPartingSection({ state, setState, sourceType, errors }: CriteriaPaneProps) {
  const { byInterval } = useLiveCaps(sourceType);
  const [bulkModal, setBulkModal] = useState<BulkModalState>(CLOSED_BULK_MODAL);
  const entries = useMemo(() => convertDayPartingToArray(state.dayHourRestrictions), [state.dayHourRestrictions]);
  const dayErrors = Array.isArray(errors?.criteriaDayHourRestrictions)
    ? (errors!.criteriaDayHourRestrictions as Record<string, unknown>[])
    : [];

  const capFor = (type: CapTypes | undefined) =>
    type ? state.capConfiguration?.caps?.find((cap) => cap.type === type) : undefined;

  const setEntries = useCallback(
    (next: DayPartingEntry[]) => setState((s) => ({ ...s, dayHourRestrictions: convertArrayToDayParting(next) })),
    [setState]
  );

  /** Sets (or, with a null amount, removes) the day-of-week cap of one type. */
  const setDayCap = (type: CapTypes, amount: number | null, restriction: CapInstance['restriction']) =>
    setState((s) => {
      const caps = s.capConfiguration?.caps || [];
      const others = caps.filter((cap) => cap.type !== type);
      const next =
        amount === null && restriction === null
          ? others
          : caps.some((cap) => cap.type === type)
            ? caps.map((cap) => (cap.type === type ? { type, amount, restriction } : cap))
            : [...caps, { type, amount, restriction }];
      return { ...s, capConfiguration: next.length > 0 ? { caps: next } : null };
    });

  /**
   * Changes a day's start or end time and re-syncs that day's cap restriction in the same
   * update. Two setState calls in one handler would read a stale state for the second.
   */
  const setDayTime = (idx: number, field: 'restrictionStart' | 'restrictionEnd', value: string) =>
    setState((s) => {
      const current = convertDayPartingToArray(s.dayHourRestrictions);
      const entry = current[idx];
      if (!entry) return s;
      const updated: DayPartingEntry = { ...entry, [field]: value };
      const nextEntries = current.map((e, i) => (i === idx ? updated : e));

      const dowType = DAY_TO_DOW_TYPE[entry.restrictionDay.toLowerCase()] as CapTypes | undefined;
      const caps = s.capConfiguration?.caps || [];
      const capConfiguration =
        dowType && caps.some((cap) => cap.type === dowType)
          ? {
              caps: caps.map((cap) =>
                cap.type === dowType
                  ? {
                      ...cap,
                      restriction: toCapRestriction(
                        updated.restriction || 'Available',
                        updated.restrictionStart || '',
                        updated.restrictionEnd || ''
                      ),
                    }
                  : cap
              ),
            }
          : s.capConfiguration;

      return { ...s, dayHourRestrictions: convertArrayToDayParting(nextEntries), capConfiguration };
    });

  const applyBulk = () => {
    const days = bulkModal.action ? BULK_ACTION_DAY_MAPPING[bulkModal.action] : undefined;
    if (!days) return;
    const partial = bulkModal.restriction === 'Partially Restricted';
    setEntries(
      entries.map((entry) =>
        days.includes(entry.restrictionDay.toLowerCase())
          ? {
              restrictionDay: entry.restrictionDay,
              restriction: bulkModal.restriction,
              restrictionStart: partial ? bulkModal.startTime : '',
              restrictionEnd: partial ? bulkModal.endTime : '',
            }
          : entry
      )
    );
    setBulkModal(CLOSED_BULK_MODAL);
  };

  const openBulk = (action: BulkAction) => setBulkModal({ ...CLOSED_BULK_MODAL, isOpen: true, action });

  return (
    <CriteriaSection
      title='Day Partings & Caps'
      help={HELP.dayParting}
      actions={
        <>
          <Button color='outline-primary' onClick={() => openBulk('all')}>
            Set All
          </Button>
          <Button color='outline-primary' onClick={() => openBulk('weekdays')}>
            Set Weekdays
          </Button>
          <Button color='outline-primary' onClick={() => openBulk('weekend')}>
            Set Weekend
          </Button>
        </>
      }
    >
      <Box sx={{ containerType: 'inline-size', containerName: 'dayparting' }}>
        <Box
          aria-hidden
          sx={{
            display: 'none',
            [DAY_TABLE_QUERY]: {
              display: 'flex',
              columnGap: 2,
              pb: 0.75,
              mb: 1,
              borderBottom: 1,
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{ flex: '0 0 6rem' }} />
          <Box sx={{ flex: '1 1 auto', display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
            {DAY_COLUMNS.map((column) => (
              <Typography key={column} variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {column}
              </Typography>
            ))}
          </Box>
        </Box>
        <Stack divider={<Divider flexItem />} spacing={1}>
          {entries.map((dp, idx) => {
            const entryError = dayErrors[idx] || {};
            const errorFor = (key: string) =>
              typeof entryError[key] === 'string' ? (entryError[key] as string) : undefined;
            const dowType = DAY_TO_DOW_TYPE[dp.restrictionDay.toLowerCase()] as CapTypes | undefined;
            const dowCap = capFor(dowType);
            const partial = dp.restriction === 'Partially Restricted';
            const live = dowType ? byInterval[dowType.toLowerCase()] : undefined;

            return (
              // The day name sits beside its controls when there is room and above them when there
              // isn't. The controls keep fixed slots (an empty one where a control doesn't apply)
              // so the seven days line up in columns and read like a table.
              <Box
                key={dp.restrictionDay}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: 2,
                  rowGap: 0.5,
                  alignItems: 'flex-end',
                  // Wide enough for one line per day: the header above names the columns, so the
                  // per-row labels step aside (still there for screen readers).
                  [DAY_TABLE_QUERY]: {
                    flexWrap: 'nowrap',
                    alignItems: 'flex-start',
                    [`& .${FIELD_LABEL_CLASS}`]: visuallyHidden,
                    '& > .day-parting-controls': { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' },
                  },
                }}
              >
                <Typography
                  component='h4'
                  variant='body2'
                  sx={{ flex: '0 0 6rem', fontWeight: 600, lineHeight: '40px' }}
                >
                  {capitalize(dp.restrictionDay)}
                </Typography>
                <Box
                  className='day-parting-controls'
                  sx={{
                    flex: '1 1 30rem',
                    minWidth: 0,
                    display: 'grid',
                    gap: 1.5,
                    alignItems: 'start',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 9.5rem), 1fr))',
                  }}
                >
                  <SelectSimple
                    label='Restriction'
                    value={dp.restriction || 'Available'}
                    onChange={(v) =>
                      setEntries(
                        entries.map((e, i) =>
                          i === idx
                            ? {
                                restrictionDay: e.restrictionDay,
                                restriction: v,
                                restrictionStart: e.restrictionStart || '',
                                restrictionEnd: e.restrictionEnd || '',
                              }
                            : e
                        )
                      )
                    }
                    options={restrictionTypes}
                    error={errorFor('restriction')}
                  />
                  {partial ? (
                    <>
                      <TimeSelect
                        label='Start Time'
                        value={dp.restrictionStart || ''}
                        onChange={(v) => setDayTime(idx, 'restrictionStart', v)}
                        error={errorFor('restrictionStart')}
                      />
                      <TimeSelect
                        label='End Time'
                        value={dp.restrictionEnd || ''}
                        onChange={(v) => setDayTime(idx, 'restrictionEnd', v)}
                        error={errorFor('restrictionEnd')}
                      />
                    </>
                  ) : (
                    <>
                      <Box aria-hidden />
                      <Box aria-hidden />
                    </>
                  )}
                  {dp.restriction !== 'Unavailable' && (
                    <Fragment>
                      <Input
                        label='Cap Amount'
                        type='number'
                        value={dowCap?.amount?.toString() ?? ''}
                        placeholder='No cap'
                        onChange={(v) => {
                          if (!dowType) return;
                          const amount = v === '' ? null : Number(v);
                          if (amount === null) setDayCap(dowType, null, null);
                          else
                            setDayCap(
                              dowType,
                              amount,
                              toCapRestriction(dp.restriction, dp.restrictionStart || '', dp.restrictionEnd || '')
                            );
                        }}
                      />
                      <ReadOnlyValue label='Current Cap Amount' value={display(live)} />
                    </Fragment>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <BulkDayPartingModal
        isOpen={bulkModal.isOpen}
        action={bulkModal.action}
        restriction={bulkModal.restriction}
        startTime={bulkModal.startTime}
        endTime={bulkModal.endTime}
        onRestrictionChange={(restriction) => setBulkModal((prev) => ({ ...prev, restriction }))}
        onStartTimeChange={(startTime) => setBulkModal((prev) => ({ ...prev, startTime }))}
        onEndTimeChange={(endTime) => setBulkModal((prev) => ({ ...prev, endTime }))}
        onApply={applyBulk}
        onCancel={() => setBulkModal(CLOSED_BULK_MODAL)}
      />
    </CriteriaSection>
  );
}

const hasDayParting = (s: CriteriaState) =>
  convertDayPartingToArray(s.dayHourRestrictions).some((e) => e.restriction && e.restriction !== 'Available') ||
  (s.capConfiguration?.caps || []).some((cap) => cap.type.startsWith('DOW_') && cap.amount != null);

/* ---------------------------------------------------------------------------------- Registry */

export const CAPS_SECTIONS: CriteriaSectionDef[] = [
  {
    id: 'dailyLimit',
    tab: 'daily',
    title: 'Daily Limit',
    fields: ['Daily Limit', 'Daily Limit Answers'],
    aliases: ['limit', 'daily', 'cap'],
    stateKeys: ['dailyLimit', 'dailyLimitAnswers'],
    appliesTo: ({ sourceType }) => sourceType === 'offer',
    isConfigured: (s) => hasValue(s.dailyLimit) || hasValue(s.dailyLimitAnswers),
    Component: DailyLimitSection,
  },
  {
    id: 'capInterval',
    tab: 'daily',
    title: 'Cap Interval',
    fields: ['Cap Interval'],
    aliases: ['Cap Amount', 'Current Cap Amount', 'monthly', 'lifetime', 'daily', 'cap'],
    stateKeys: ['capInterval', 'capAmount'],
    isConfigured: (s) => hasValue(s.capInterval),
    Component: CapIntervalSection,
  },
  {
    id: 'capDependencies',
    tab: 'daily',
    title: 'Cap Dependencies',
    fields: ['Dependency Mode', 'Dependent Offers', 'Dependent Offer Actions'],
    aliases: ['dependency', 'depends', 'capped'],
    stateKeys: ['capDependency'],
    isConfigured: (s) => Boolean(s.capDependency?.offerIds?.length || s.capDependency?.offerActionIds?.length),
    Component: CapDependenciesSection,
  },
  {
    id: 'capGroup',
    tab: 'daily',
    title: 'Cap Group',
    fields: [],
    aliases: ['Cap Group', 'Cap Amount', 'group cap', 'shared cap'],
    stateKeys: [],
    appliesTo: ({ isOfferAction }) => isOfferAction,
    isConfigured: (_s, { capGroupConfigured }) => capGroupConfigured,
    Component: CapGroupCriteriaSection,
  },
  {
    id: 'dayParting',
    tab: 'daily',
    title: 'Day Partings & Caps',
    fields: ['Restriction', 'Cap Amount'],
    aliases: [
      'Start Time',
      'End Time',
      'Set All',
      'Set Weekdays',
      'Set Weekend',
      'day parting',
      'schedule',
      'hours',
      'weekday',
      'weekend',
      'time',
    ],
    stateKeys: ['dayHourRestrictions', 'capConfiguration'],
    isConfigured: hasDayParting,
    Component: DayPartingSection,
  },
];
